/**
 * Centralised logger for the ada-tool editor (ES module).
 *
 * This is the ES module counterpart to `../log.js` (which sets
 * `window.adatool.log` for non-module SPA scripts). Editor files are loaded as
 * ES modules and import from this file:
 *
 *     import { log } from "./log.js";
 *     log.warn("[editor] something unexpected:", err);
 *
 * The `[ada-tool]` tag is prepended automatically; call sites should add
 * a component qualifier after it (e.g. `"[editor]"`, `"[session-store]"`,
 * `"[gesture:<name>]"`) but do NOT repeat `[ada-tool]` — this module
 * prepends it.
 *
 * WHY A SEPARATE FILE FROM ../log.js?
 * ------------------------------------
 * Browser ES modules cannot import from non-module scripts, and vice versa.
 * The SPA's non-module scripts use `window.adatool.log` (set by `../log.js`
 * loaded early via `<script>`). The editor's ES module graph uses this
 * file. Both share the same `[ada-tool]` prefix convention so the
 * visual-correctness U2 predicate sees uniform output.
 *
 * TOPIC-GATED DEBUG / TRACE (Phase 5 of debugging-parity Epic, 260524)
 * --------------------------------------------------------------------
 * `.debug(topic, ...)` / `.trace(topic, ...)` mirror C# `DebugTrace.Emit` +
 * Python `web.debug_trace.DebugTrace.emit`. The browser has no env vars,
 * so the enabled-topic set is sourced from URL param `?adaDebugTrace=` OR
 * `localStorage.getItem('ada_debug_trace')`. URL wins when both are
 * present. Lookup is cached per page-load; call `log.setDebugTopics([...])`
 * to change mid-session.
 *
 * Design + cross-language parity table: `docs/dev/LOGGING.md`.
 */
const TAG = "[ada-tool]";
let _enabledTopics = null;
function _parseTopicString(raw) {
    if (!raw || typeof raw !== "string")
        return new Set();
    return new Set(raw.split(",").map((t) => t.trim().toLowerCase()).filter((t) => t.length > 0));
}
function _resolveEnabledTopics() {
    if (_enabledTopics !== null)
        return _enabledTopics;
    let raw = "";
    try {
        const params = new URLSearchParams(window.location.search);
        raw = params.get("adaDebugTrace") ?? "";
    }
    catch (_e) {
        // No-op: URL search may be unavailable in odd embed contexts.
    }
    if (!raw) {
        try {
            raw = window.localStorage.getItem("ada_debug_trace") ?? ""; // noqa: no-global-read-in-frontend-production — dom-accessor-migration-pending
        }
        catch (_e) {
            // No-op: localStorage may be blocked (Safari private mode, etc.).
        }
    }
    _enabledTopics = _parseTopicString(raw);
    return _enabledTopics;
}
function _emitIfEnabled(sink, topic, args) {
    const enabled = _resolveEnabledTopics();
    const topicStr = String(topic || "").toLowerCase();
    if (!enabled.has(topicStr))
        return;
    sink(TAG, `TRACE[${topicStr}]`, ...args);
}
export const log = {
    warn(...args) {
        // eslint-disable-next-line no-restricted-globals -- canonical logger sink; this IS the adaLog implementation for editor.
        console.warn(TAG, ...args);
    },
    error(...args) {
        // eslint-disable-next-line no-restricted-globals -- canonical logger sink; this IS the adaLog implementation for editor.
        console.error(TAG, ...args);
    },
    info(...args) {
        // eslint-disable-next-line no-restricted-globals -- canonical logger sink; this IS the adaLog implementation for editor.
        console.info(TAG, ...args);
    },
    debug(topic, ...args) {
        // eslint-disable-next-line no-restricted-globals -- canonical logger sink; topic-gated.
        _emitIfEnabled(console.debug.bind(console), topic, args);
    },
    trace(topic, ...args) {
        // eslint-disable-next-line no-restricted-globals -- canonical logger sink; topic-gated.
        _emitIfEnabled(console.trace.bind(console), topic, args);
    },
    setDebugTopics(topics) {
        if (topics === null || topics === undefined) {
            _enabledTopics = new Set();
            return;
        }
        if (Array.isArray(topics)) {
            _enabledTopics = new Set(topics
                .map((t) => String(t).trim().toLowerCase())
                .filter((t) => t.length > 0));
            return;
        }
        _enabledTopics = _parseTopicString(String(topics));
    },
    isDebugTopicEnabled(topic) {
        return _resolveEnabledTopics().has(String(topic || "").toLowerCase());
    },
};
